/**
 * Feuille de brouillon — écriture mathématique d'une ligne.
 *
 * Une division s'affiche en fraction empilée : le numérateur est le produit qui
 * précède le « ÷ » (jusqu'à un +, −, =, « ou » ou une comparaison), le dénominateur
 * le facteur qui suit (une fonction avec sa parenthèse : « 1 ÷ exp(x − 2) »). Chaque jeton garde le numéro du bout dont il vient (`o`) : c'est
 * ce qui permet de toucher un bout d'une ligne du dessus pour le recopier.
 *
 * « k parmi n » (moteur v27) s'affiche en coefficient binomial : n au-dessus
 * de k, entre deux grandes parenthèses. « parmi » lie plus fort que × et ÷ :
 * ses opérandes sont le facteur juste avant et juste après.
 *
 * Une matrice (moteur v30) s'écrit « [1, 2 ; 3, 4] » : « , » sépare les coefficients
 * d'une ligne, « ; » les lignes. Elle s'affiche en tableau entre deux grandes
 * parenthèses (élément `m`) ; un crochet sans son crochet fermant (ligne en cours)
 * reste écrit tel quel.
 *
 * Une trace Python (moteur v52) écrit des LISTES « [1, 3, 5] » : sur une feuille Python
 * (`lists`), un crochet reste écrit tel quel, jamais une matrice.
 *
 * Une intégrale (moteur v55) : « ∫(0 ; 1) » et le crochet fermé « ](0 ; 1) » restent des
 * jetons (dessinés avec leurs bornes par la vue) ; une barre « ÷ » ne remonte jamais
 * au-delà d'eux ni d'un crochet ouvert « [ ».
 *
 * Une puissance générale (moteur v2) « 1,05^n », « (2 ÷ 3)^(−2) » s'affiche en exposant
 * (élément `s`) : le « ^ » disparaît, les parenthèses de l'exposant aussi quand elles
 * sont fermées (une ligne en cours garde « ^( » tel quel).
 *
 * Portage du prototype (mathTree / fracs). Pur : aucun DOM.
 */

import { integralBoundsOf, isSumAtom } from '@jeraisonne/reasoner';
import { isFrac, type LineItem } from './line-items';

export interface MathPart {
  items: MathItem[];
  /** parenthèses retirées autour d'une case : (a − b) seul au numérateur */
  hidden?: [number | undefined, number | undefined];
}
export type MathItem =
  | { kind: 't'; t: string; o?: number }
  /** `open` : parenthèse jamais fermée (ligne en cours) */
  | { kind: 'g'; items: MathItem[]; o1?: number; o2?: number; open?: boolean }
  /** exposant d'une puissance générale : `o` = le bout « ^ », `o1` / `o2` = ses parenthèses */
  | { kind: 's'; items: MathItem[]; o?: number; o1?: number; o2?: number }
  /** `whole` : fraction à deux cases (un seul bout) ; sinon, barre issue d'un « ÷ » */
  | { kind: 'f'; num: MathPart; den: MathPart; o?: number; whole?: boolean }
  /**
   * « k parmi n » : `top` = n, `bottom` = k ; `o` = le bout « parmi » ;
   * `hidden` = parenthèses retirées autour de (k parmi n)
   */
  | {
      kind: 'b';
      top: MathPart;
      bottom: MathPart;
      o?: number;
      hidden?: [number | undefined, number | undefined];
    }
  /**
   * matrice (moteur v30) : `rows[i][j]` = le coefficient (i, j) ; `o1` / `o2` = les bouts
   * « [ » et « ] » ; `whole` : toute la matrice vient d'un seul bout (recopiée d'un coup)
   */
  | { kind: 'm'; rows: MathItem[][][]; o1?: number; o2?: number; whole?: boolean };

type Token = string | Extract<MathItem, { kind: 'f' }>;

function tk(it: MathItem | undefined): string {
  return it && it.kind === 't' ? it.t : '';
}

function unwrap(xs: MathItem[]): MathPart {
  const first = xs[0];
  if (xs.length === 1 && first && first.kind === 'g') {
    return { items: first.items.slice(), hidden: [first.o1, first.o2] };
  }
  return { items: xs };
}

const POSTFIX = /^(²|!|%)$/;
/** ce qui borne le numérateur d'une barre « ÷ » à gauche */
const NUM_STOP = /^(\+|−|=|ou|<|>|≤|≥|≠|≈|≡|;|→|∼)$/;
/** une fonction suivie de sa parenthèse forme un seul facteur */
const FUNC_NAME = /^(exp|ln|log|cos|sin|tan|arccos|arcsin|arctan|arg)$/;
/** un exposant posé d'un bout (« ³ », « ¹⁰ ») */
const SUP_TOKEN = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]+$/;

/** Opérande à gauche de « parmi » : début (inclus) du facteur qui finit en i − 1. */
function leftOperand(items: MathItem[], i: number): number {
  let j = i - 1;
  while (j >= 0 && POSTFIX.test(tk(items[j]))) j--;
  return j;
}

/** Opérande à droite de « parmi » : fin (exclue) du facteur qui commence en i + 1. */
function rightOperand(items: MathItem[], i: number): number {
  let k = i + 1;
  if (tk(items[k]) === '√' || tk(items[k]) === '−') k++;
  if (k >= items.length) return -1;
  k++;
  while (k < items.length && POSTFIX.test(tk(items[k]))) k++;
  return k;
}

function soleBinom(it: MathItem): MathItem {
  if (it.kind !== 'g' || it.items.length !== 1) return it;
  const only = it.items[0]!;
  if (only.kind !== 'b' || only.hidden) return it;
  return { ...only, hidden: [it.o1, it.o2] };
}

function binoms(input: MathItem[]): MathItem[] {
  const items: MathItem[] = input.map((it) =>
    it.kind === 'g' ? soleBinom({ ...it, items: binoms(it.items) }) : it,
  );
  for (let i = 0; i < items.length; i++) {
    if (tk(items[i]) !== 'parmi') continue;
    const j = leftOperand(items, i);
    const k = rightOperand(items, i);
    const word = items[i] as Extract<MathItem, { kind: 't' }>;
    if (j < 0 || k < 0 || !/^(\+|−|×|÷|=|ou|parmi)?$/.test(tk(items[j - 1]))) {
      continue;
    }
    const bottom = unwrap(items.slice(j, i));
    const top = unwrap(items.slice(i + 1, k));
    items.splice(j, k - j, { kind: 'b', top, bottom, o: word.o });
    i = j;
  }
  return items;
}

/** « ^ » suivi de son exposant (un jeton, ou une parenthèse fermée) → un exposant. */
function sups(input: MathItem[]): MathItem[] {
  const items: MathItem[] = input.map((it) =>
    it.kind === 'g' ? { ...it, items: sups(it.items) } : it,
  );
  for (let i = 0; i < items.length - 1; i++) {
    const caret = items[i]!;
    if (tk(caret) !== '^' || i === 0 || caret.kind !== 't') continue;
    const e = items[i + 1]!;
    if (e.kind === 'g' && !e.open && e.items.length) {
      items.splice(i, 2, { kind: 's', items: e.items, o: caret.o, o1: e.o1, o2: e.o2 });
    } else if (e.kind === 't' && /^[\p{L}\d,]+$/u.test(e.t)) {
      items.splice(i, 2, { kind: 's', items: [e], o: caret.o });
    }
  }
  return items;
}

function fracs(input: MathItem[]): MathItem[] {
  const items: MathItem[] = input.map((it) =>
    it.kind === 'g' || it.kind === 's'
      ? { ...it, items: fracs(it.items) }
      : it.kind === 'b'
        ? {
            ...it,
            top: { ...it.top, items: fracs(it.top.items) },
            bottom: { ...it.bottom, items: fracs(it.bottom.items) },
          }
        : it,
  );
  let i: number;
  while ((i = items.findIndex((it) => tk(it) === '÷')) >= 0) {
    let j = i;
    // le numérateur s'arrête aussi à une comparaison (« e^a ≤ 1 ÷ e^b » : 1 seul en haut),
    // au signe ∫ et aux crochets d'une intégrale (moteur v55 : « ∫(0 ; 1) x ÷ 2 dx »),
    // au signe Σ d'une somme (moteur v59 : « Σ(k = 1 ; n) 1 ÷ (n + k) »)
    while (
      j > 0 &&
      !NUM_STOP.test(tk(items[j - 1])) &&
      tk(items[j - 1]) !== '[' &&
      !integralBoundsOf(tk(items[j - 1])) &&
      !isSumAtom(tk(items[j - 1]))
    )
      j--;
    let k = i + 1;
    if (tk(items[k]) === '√' || tk(items[k]) === '−') k++;
    // une fonction garde sa parenthèse au dénominateur : « 1 ÷ exp(x − 2) »
    if (FUNC_NAME.test(tk(items[k])) && items[k + 1]?.kind === 'g') k++;
    k++;
    // le carré, le pourcentage, un exposant (moteur v31 : « ÷ (x + 3)³ ») suivent le dénominateur
    while (
      tk(items[k]) === '²' ||
      tk(items[k]) === '%' ||
      SUP_TOKEN.test(tk(items[k])) ||
      items[k]?.kind === 's'
    )
      k++;
    const num = items.slice(j, i);
    const den = items.slice(i + 1, Math.min(k, items.length));
    const bar = items[i] as Extract<MathItem, { kind: 't' }>;
    if (!num.length || !den.length || k > items.length) {
      items[i] = { kind: 't', t: '/', o: bar.o };
      continue;
    }
    items.splice(j, k - j, { kind: 'f', num: unwrap(num), den: unwrap(den), o: bar.o });
  }
  return items;
}

/** Le crochet « ] » qui ferme celui de `open`, ou −1 (matrice inachevée). */
function closingBracket(atoms: Token[], open: number): number {
  let depth = 0;
  for (let i = open; i < atoms.length; i++) {
    const a = atoms[i];
    if (a === '[' || a === '(') depth++;
    if (a === ']' || a === ')') depth--;
    if (depth === 0) return a === ']' ? i : -1;
    if (depth < 0) return -1;
  }
  return -1;
}

/** Une matrice : ses lignes (« ; ») et ses coefficients (« , »), chacun mis en forme. */
function matrixItem(
  atoms: Token[],
  owners: (number | undefined)[] | undefined,
  open: number,
  close: number,
): Extract<MathItem, { kind: 'm' }> {
  const rows: MathItem[][][] = [];
  let row: MathItem[][] = [];
  let cell: Token[] = [];
  let cellOwners: (number | undefined)[] = [];
  let depth = 0;
  const endCell = () => {
    row.push(mathTree(cell, owners ? cellOwners : undefined));
    cell = [];
    cellOwners = [];
  };
  for (let i = open + 1; i < close; i++) {
    const a = atoms[i]!;
    if (a === '(' || a === '[') depth++;
    if (a === ')' || a === ']') depth--;
    if (depth === 0 && (a === ',' || a === ';')) {
      endCell();
      if (a === ';') {
        rows.push(row);
        row = [];
      }
      continue;
    }
    cell.push(a);
    cellOwners.push(owners?.[i]);
  }
  endCell();
  rows.push(row);
  const o1 = owners?.[open];
  const o2 = owners?.[close];
  let whole = o1 !== undefined;
  for (let i = open; whole && i <= close; i++) if (owners?.[i] !== o1) whole = false;
  return { kind: 'm', rows, o1, o2, ...(whole ? { whole: true } : {}) };
}

/** Options d'affichage : `lists` = feuille Python (moteur v52), « [ » écrit une liste. */
export interface MathOptions {
  lists?: boolean;
}

/** Arbre d'affichage d'une suite d'atomes (owners : n° du bout de chaque atome). */
export function mathTree(
  atoms: Token[],
  owners?: (number | undefined)[],
  opts: MathOptions = {},
): MathItem[] {
  let pos = 0;
  function group(depth: number): { items: MathItem[]; close?: number; closed?: boolean } {
    const items: MathItem[] = [];
    while (pos < atoms.length) {
      const p = pos++;
      const a = atoms[p]!;
      const o = owners ? owners[p] : undefined;
      const close = a === '[' && !opts.lists ? closingBracket(atoms, p) : -1;
      if (close > 0) {
        items.push(matrixItem(atoms, owners, p, close));
        pos = close + 1;
      } else if (a === '(') {
        const g = group(depth + 1);
        items.push({
          kind: 'g',
          items: g.items,
          o1: o,
          o2: g.close,
          ...(g.closed ? {} : { open: true }),
        });
      } else if (a === ')' && depth > 0) return { items, close: o, closed: true };
      else if (typeof a === 'string') items.push({ kind: 't', t: a, o });
      else items.push(a);
    }
    return { items };
  }
  return fracs(binoms(sups(group(0).items)));
}

function displayTokens(
  items: LineItem[],
  opts: MathOptions = {},
): { atoms: Token[]; owners: number[] } {
  const atoms: Token[] = [];
  const owners: number[] = [];
  items.forEach((c, ci) => {
    if (isFrac(c)) {
      atoms.push({
        kind: 'f',
        whole: true,
        num: { items: mathTree(displayTokens(c.num, opts).atoms, undefined, opts) },
        den: { items: mathTree(displayTokens(c.den, opts).atoms, undefined, opts) },
        o: ci,
      });
      owners.push(ci);
    } else {
      c.atoms.forEach((a) => {
        atoms.push(a);
        owners.push(ci);
      });
    }
  });
  return { atoms, owners };
}

/** Arbre d'affichage d'une ligne de bouts. */
export function lineTree(items: LineItem[], opts: MathOptions = {}): MathItem[] {
  const d = displayTokens(items, opts);
  return mathTree(d.atoms, d.owners, opts);
}

/** Arbre d'affichage de simples atomes (bout de palette contenant une division). */
export function atomsTree(atoms: string[], opts: MathOptions = {}): MathItem[] {
  return mathTree(atoms, undefined, opts);
}
